import React, { useMemo } from "react";

import PropTypes from "prop-types";

import { useAllocationProject } from "src/contexts/global/GlobalContext";
import { useGroupTypes } from "src/contexts/global/WorkspaceContext";
import useFulfillVacantRole from "src/components/Person/PersonItem/sub-components/hooks/useFulfillVacantRole";
import ConfiguredActionMenu from "src/components/ConfiguredActionMenu";
import { copywriting } from "src/components/Person/PersonItem/PersonItem.config";
import { useAuth0 } from "src/contexts/auth0Provider";
import PersonPropType from "src/custom-prop-types/person";
import GroupPropType from "src/custom-prop-types/group";

const PERSON_ACTION_MENU_TYPE = {
  EDIT_ALLOCATIONS: "edit_allocations",
  NOTIFY_TEAM_CHANGES: "notify_team_changes",
  FULFILL_VACANT_ROLE: "fulfill_vacant_role",
  REMOVE_VACANT_ROLE: "remove_vacant_role",
};

const getTargetGroup = (group, person) => {
  if (group) {
    return group;
  }

  if (person?.isPlaceholder && person?.allocations?.length > 0) {
    const { allocations } = person;
    const { targetGroupId } = allocations[0];

    return person.memberOf.find((m) => m.id === targetGroupId);
  }

  return null;
};

const PersonActionsMenu = ({
  direction = "right-start",
  onEditingAllocations,
  canChangeAllocations,
  onDisplayReportTeamChange,
  canReportTeamChange,
  person,
  group,
}) => {
  const groupTypes = useGroupTypes();
  const { user } = useAuth0();
  const allocationProject = useAllocationProject();
  const targetGroup = getTargetGroup(group, person);

  const {
    canShowFulfillButton,
    canRemoveVacantRole,
    handleAddMember,
    handleRemoveVacantRole,
    vacantRoleLabel,
  } = useFulfillVacantRole({
    person,
    group: targetGroup,
    user,
    groupTypes,
    allocationProject,
  });

  // Logical order: Allocation actions → Placeholder actions → Feedback
  const actions = useMemo(() => {
    return [
      {
        type: PERSON_ACTION_MENU_TYPE.EDIT_ALLOCATIONS,
        className: "guided-tour-team-details-explore-in-org",
        label: copywriting.EDIT_ALLOCATIONS,
        onClick: onEditingAllocations,
        isEnabled: canChangeAllocations,
      },
      {
        type: PERSON_ACTION_MENU_TYPE.FULFILL_VACANT_ROLE,
        className: "guided-tour-fulfill-vacant-role-in-org",
        label: `Fulfil ${vacantRoleLabel}`,
        onClick: handleAddMember,
        isEnabled: canShowFulfillButton && canChangeAllocations,
      },
      {
        type: PERSON_ACTION_MENU_TYPE.REMOVE_VACANT_ROLE,
        className: "guided-tour-remove-vacant-role-in-org",
        label: `Remove ${vacantRoleLabel}`,
        onClick: handleRemoveVacantRole,
        isEnabled: canRemoveVacantRole && canChangeAllocations,
      },
      {
        type: PERSON_ACTION_MENU_TYPE.NOTIFY_TEAM_CHANGES,
        className: "guided-tour-team-details-explore-in-org",
        label: copywriting.NOTIFY_TEAM_CHANGES,
        onClick: onDisplayReportTeamChange,
        isEnabled: canReportTeamChange,
      },
    ].filter((action) => action.isEnabled);
  }, [
    canChangeAllocations,
    onEditingAllocations,
    canReportTeamChange,
    onDisplayReportTeamChange,
    canShowFulfillButton,
    canRemoveVacantRole,
    handleAddMember,
    handleRemoveVacantRole,
    vacantRoleLabel,
  ]);

  if (!actions.length) {
    return null;
  }

  return (
    <ConfiguredActionMenu direction={direction} ml="r" actions={actions} />
  );
};

PersonActionsMenu.propTypes = {
  direction: PropTypes.string,
  canChangeAllocations: PropTypes.bool,
  onEditingAllocations: PropTypes.func,
  canReportTeamChange: PropTypes.bool,
  onDisplayReportTeamChange: PropTypes.func,
  person: PersonPropType,
  group: GroupPropType,
};

export default PersonActionsMenu;
